import React, { useMemo, useState } from 'react'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import IconButton from '@mui/material/IconButton'
import useStyles from '../_styles'
import { PatientStepProps } from '../_props'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { EmptySearchPatientIcon } from '../../../../assets/icons/icons'
import { Button } from '@mui/material'
import { getPatientBirthLabel, getPatientSexLabel, getCivilityLabel } from '../../../Patient/History/_utils'
import PlannedExpertiseFillAddressDialog from '../../plannedExpertiseCard/PlannedExpertiseFillAddressDialog'
import { PatientForActDtoIds, useGetIdsHealthcareservicesHcsIdPatientsForAct } from '@toktokdoctech/tokapi-client'
import useCurrentHcs from '../../../../hooks/useCurrentHcs'
import { useDebouncedCallback } from '@workspace/frontend-kit'

export default function PatientStep({ onDataChange, onSelectAndContinue, defaultValues }: PatientStepProps) {
    const classes = useStyles()
    const currentHcs = useCurrentHcs()

    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [selectedPatientId, setSelectedPatientId] = useState<string | null>(defaultValues?.patientId ?? null)
    const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false)
    const [selectedPatientForAddress, setSelectedPatientForAddress] = useState<string | null>(null)

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setDebouncedSearchTerm(value)
    }, 300)

    const { data: patientsData, isLoading } = useGetIdsHealthcareservicesHcsIdPatientsForAct(currentHcs.id, {
        search: debouncedSearchTerm || undefined,
        skip: 0,
        limit: 20,
        sortOrder: 'Asc',
    })

    const patientLabel = useMemo(() => {
        const patientLibelle = defaultValues?.patientLibelle
        return patientLibelle ? `Patient sélectionné : ${patientLibelle}` : 'Sélectionnez le patient'
    }, [defaultValues?.patientLibelle])

    const patients = patientsData?.items ?? []
    const hasSearchTerm = debouncedSearchTerm.length > 0

    const handleSelectPatient = (patient: PatientForActDtoIds) => {
        const patientLibelle = `${patient.fullName} (né(e) le ${patient.birthDate}, ${patient.age} ans, Sexe ${patient.sex})`

        onDataChange?.({
            patientId: patient.id,
            patientLibelle,
            patientData: {
                id: patient.id,
                fullName: patient.fullName,
                sex: patient.sex,
                age: patient.age,
                birthDate: patient.birthDate,
                isInMate: patient.isInMate,
                hasAddress: patient.hasAddress,
            },
        })
        setSelectedPatientId(patient.id)
        onSelectAndContinue?.()
    }

    const isAddressMissing = (patient: PatientForActDtoIds) => !patient.isInMate && !patient.hasAddress

    const handlePatientClick = (patient: PatientForActDtoIds) => {
        if (isAddressMissing(patient)) {
            return
        }
        handleSelectPatient(patient)
    }

    const handleResetSearch = () => {
        setSearchTerm('')
        setDebouncedSearchTerm('')
        debouncedSearch('')
    }

    const handleAddressDialogClose = () => {
        setIsAddressDialogOpen(false)
        setSelectedPatientForAddress(null)
    }

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setSearchTerm(value)
        debouncedSearch(value)
    }

    return (
        <div className={classes.stepContent}>
            <Typography className={classes.patientLabel}>{patientLabel}</Typography>

            <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher un patient"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon className={classes.clearSearchIcon} />
                        </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={handleResetSearch}
                                edge="end"
                                aria-label="Effacer la recherche"
                            >
                                <ClearIcon className={classes.clearSearchIcon} fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Paper className={classes.patientList}>
                {isLoading && (
                    <div className={classes.noResults}>
                        <Typography>Chargement...</Typography>
                    </div>
                )}
                {!isLoading && hasSearchTerm && patients.length > 0 && (
                    <div className={classes.resultsCount}>
                        <Typography variant="body2">
                            {patients.length} résultat{patients.length > 1 ? 's' : ''} pour "{debouncedSearchTerm}"
                        </Typography>
                    </div>
                )}

                {!isLoading && patients.length > 0 ? (
                    <List>
                        {patients.map((patient) => {
                            const addressMissing = isAddressMissing(patient)
                            return (
                                <React.Fragment key={patient.id}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => handlePatientClick(patient)}
                                            className={`
                        ${addressMissing ? classes.disabledPatient : ''}
                        ${selectedPatientId === patient.id && !addressMissing ? classes.selectedPatient : ''}
                      `}
                                        >
                                            <ListItemText
                                                primary={`${getCivilityLabel(patient.sex)} ${patient.fullName}`}
                                                secondary={`${getPatientBirthLabel(patient.sex)} le ${
                                                    patient.birthDate
                                                } (${patient.age} ans) - Sexe ${getPatientSexLabel(patient.sex)}`}
                                                primaryTypographyProps={{ className: classes.patientName }}
                                                secondaryTypographyProps={{ className: classes.patientInfo }}
                                            />

                                            {selectedPatientId === patient.id && !addressMissing && (
                                                <CheckCircleIcon className={classes.checkIcon} />
                                            )}

                                            {addressMissing && (
                                                <div className={classes.missingRppsWrapper}>
                                                    <Typography className={classes.missingRpps}>
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="8"
                                                            height="8"
                                                            viewBox="0 0 8 8"
                                                            fill="none"
                                                            className={classes.missingRppsIcon}
                                                        >
                                                            <circle cx="4" cy="4" r="4" fill="#FB7D08" />
                                                        </svg>
                                                        Adresse manquante
                                                    </Typography>
                                                    <Button
                                                        className={classes.missingRppsButton}
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            setSelectedPatientForAddress(patient.id)
                                                            setIsAddressDialogOpen(true)
                                                        }}
                                                    >
                                                        Renseigner
                                                    </Button>
                                                </div>
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                </React.Fragment>
                            )
                        })}
                    </List>
                ) : (
                    !isLoading && (
                        <div className={classes.noResults}>
                            <EmptySearchPatientIcon className={classes.searchRequesterIcon} />
                            <Typography className={classes.noPatient}>
                                Désolé, aucun patient n’a été trouvé. Vérifiez l’orthographe du nom et prénom, puis
                                réessayez
                            </Typography>
                            <Button className={classes.resetFilter} onClick={handleResetSearch}>
                                Réinitialiser la recherche
                            </Button>
                        </div>
                    )
                )}
            </Paper>

            <PlannedExpertiseFillAddressDialog
                isOpen={isAddressDialogOpen}
                plannedExpertiseId={selectedPatientForAddress || ''}
                onClose={handleAddressDialogClose}
            />
        </div>
    )
}
